<template>
  <div class="step-review">
    <v-container fluid class="pa-6 px-4">
      <v-row justify="center">
        <v-col cols="12" lg="11">

          <div v-if="importComplete" class="result-screen py-8">
            <template v-if="lastRunWasDryRun">
              <div class="text-center">
                <v-icon size="96" color="info" class="mb-4">mdi-flask-check-outline</v-icon>
                <div class="text-h4 font-weight-medium mb-3">Import Check Complete</div>
                <div class="text-body-1 grey--text mb-6">
                  Review the projected changes before committing.
                </div>
              </div>

              <v-alert v-if="dryRunReportError" type="warning" outlined dense class="mb-4">
                Could not load report details on screen. You can still download the report.
              </v-alert>

              <v-row class="mb-4" justify="center">
                <v-col cols="12" sm="6" md="2">
                  <v-card outlined class="stat-card text-center pa-3">
                    <div class="text-h5 font-weight-bold">{{ dryRunSummary.totalRows }}</div>
                    <div class="text-caption grey--text">Total</div>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="2">
                  <v-card outlined class="stat-card text-center pa-3">
                    <div class="text-h5 font-weight-bold success--text">{{ dryRunSummary.createCount }}</div>
                    <div class="text-caption grey--text">Create</div>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="2">
                  <v-card outlined class="stat-card text-center pa-3">
                    <div class="text-h5 font-weight-bold warning--text">{{ dryRunSummary.updateCount }}</div>
                    <div class="text-caption grey--text">Update</div>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="2">
                  <v-card outlined class="stat-card text-center pa-3">
                    <div class="text-h5 font-weight-bold">{{ dryRunSummary.skipCount }}</div>
                    <div class="text-caption grey--text">Skip</div>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="2">
                  <v-card outlined class="stat-card text-center pa-3">
                    <div class="text-h5 font-weight-bold error--text">{{ dryRunSummary.errorCount }}</div>
                    <div class="text-caption grey--text">Errors</div>
                  </v-card>
                </v-col>
              </v-row>

              <div class="d-flex justify-center flex-wrap mt-4" style="gap: 12px;">
                <v-btn color="primary" outlined large @click="showDryRunDetails = !showDryRunDetails">
                  <v-icon left>{{ showDryRunDetails ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}</v-icon>
                  {{ showDryRunDetails ? 'Hide Details' : 'See More Details' }}
                </v-btn>

                <v-btn v-if="reportAvailable" outlined color="primary" large @click="$emit('download-dry-run-report')">
                  <v-icon left>mdi-download</v-icon>
                  Download Report
                </v-btn>

                <v-btn
                  color="warning"
                  large
                  :disabled="!canCommitDryRun"
                  :loading="isCommitLoading"
                  @click="$emit('commit-dry-run')">
                  <v-icon left>mdi-content-save-check</v-icon>
                  Commit
                  <v-icon v-if="commitBlockedAfterDryRun" right small>mdi-alert-circle-outline</v-icon>
                </v-btn>

                <v-btn outlined color="primary" large @click="$emit('done')">
                  <v-icon left>mdi-upload</v-icon>
                  Import More
                </v-btn>
              </div>

              <div v-if="commitBlockedAfterDryRun" class="text-caption warning--text text-center mt-3">
                <v-icon small color="warning" class="mr-1">mdi-alert-circle-outline</v-icon>
                Errors were found in the dry run. Resolve them before committing.
              </div>

              <v-expand-transition>
                <div v-if="showDryRunDetails" class="mt-6">
                  <v-card outlined>
                    <v-card-title class="subtitle-1 font-weight-medium">Detailed Changes</v-card-title>
                    <v-divider></v-divider>
                    <v-card-text class="pa-0">
                      <div class="details-table-wrap">
                        <v-simple-table dense>
                          <thead>
                            <tr>
                              <th>Row #</th>
                              <th>Identifier</th>
                              <th>Action</th>
                              <th>Field</th>
                              <th>Old Value</th>
                              <th>New Value</th>
                              <th>Notes</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="(item, index) in dryRunDetails"
                              :key="`${item.rowNumber}-${item.identifier}-${index}`"
                              :class="detailsRowClass(item)">
                              <td>{{ item.rowNumber }}</td>
                              <td>{{ item.identifier }}</td>
                              <td>{{ item.action }}</td>
                              <td>{{ item.field }}</td>
                              <td>{{ item.oldValue }}</td>
                              <td>{{ item.newValue }}</td>
                              <td>{{ item.notes }}</td>
                            </tr>
                            <tr v-if="dryRunDetails.length === 0">
                              <td colspan="7" class="text-center grey--text py-4">No detailed rows available.</td>
                            </tr>
                          </tbody>
                        </v-simple-table>
                      </div>
                    </v-card-text>
                  </v-card>
                </div>
              </v-expand-transition>
            </template>

            <template v-else>
              <div class="text-center">
                <v-icon size="96" color="success" class="mb-4">mdi-check-circle</v-icon>
                <div class="text-h4 font-weight-medium mb-3">Import Successful</div>
                <div class="text-body-1 grey--text mb-2">
                  {{ summary.validRows }} product{{ summary.validRows !== 1 ? 's' : '' }}
                  imported to {{ summary.sections.length }} section{{ summary.sections.length !== 1 ? 's' : '' }}.
                </div>
                <div v-if="summary.sections.length > 0" class="d-flex justify-center flex-wrap mb-6">
                  <v-chip v-for="section in summary.sections" :key="section" small class="mx-1 mb-1">
                    {{ section }}
                  </v-chip>
                </div>
                <div class="d-flex justify-center mt-4" style="gap: 16px;">
                  <v-btn outlined color="primary" large @click="$emit('done')">
                    <v-icon left>mdi-upload</v-icon>
                    Import More
                  </v-btn>
                  <v-btn v-if="summary.sections.length > 0" color="primary" large @click="$emit('view-products')">
                    <v-icon left>mdi-magnify</v-icon>
                    View Products
                  </v-btn>
                </div>
              </div>
            </template>
          </div>

          <div v-else-if="importError" class="result-screen text-center py-8">
            <v-icon size="96" color="error" class="mb-4">mdi-alert-circle</v-icon>
            <div class="text-h4 font-weight-medium mb-3">Import Failed</div>
            <div class="text-body-1 grey--text mb-6">
              {{ importError }}
            </div>
            <v-btn color="primary" large @click="$emit('start-import')">
              <v-icon left>mdi-refresh</v-icon>
              Try Again
            </v-btn>
          </div>

          <template v-else>
            <template>
              <v-row class="mb-6 mt-2">
                <v-col cols="12" sm="4">
                  <v-card outlined class="stat-card text-center pa-4">
                    <div class="text-h3 font-weight-bold primary--text">{{ summary.totalRows }}</div>
                    <div class="text-body-2 grey--text mt-1">Total Rows</div>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-card outlined class="stat-card text-center pa-4">
                    <div class="text-h3 font-weight-bold success--text">{{ summary.validRows }}</div>
                    <div class="text-body-2 grey--text mt-1">Products to Import</div>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-card outlined class="stat-card text-center pa-4">
                    <div class="text-h3 font-weight-bold">{{ summary.sections.length }}</div>
                    <div class="text-body-2 grey--text mt-1">Section{{ summary.sections.length !== 1 ? 's' : '' }}</div>
                  </v-card>
                </v-col>
              </v-row>

              <v-card v-if="summary.sections.length > 0 || summary.categories.length > 0" outlined class="mb-4">
                <v-card-text class="pa-4">
                  <div v-if="summary.sections.length > 0" class="mb-3">
                    <div class="text-caption grey--text mb-2">Importing to sections:</div>
                    <div class="d-flex flex-wrap">
                      <v-chip v-for="section in summary.sections" :key="section" small class="mr-2 mb-1">
                        {{ section }}
                      </v-chip>
                    </div>
                  </div>
                  <div v-if="summary.categories.length > 0">
                    <div class="text-caption grey--text mb-2">Categories:</div>
                    <div class="d-flex flex-wrap">
                      <v-chip v-for="category in summary.categories.slice(0, 8)" :key="category" small outlined
                        class="mr-2 mb-1">
                        {{ category }}
                      </v-chip>
                      <v-chip v-if="summary.categories.length > 8" small text class="mr-2 mb-1">
                        +{{ summary.categories.length - 8 }} more
                      </v-chip>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-alert v-if="hasChanges" type="success" outlined class="mb-4">
                <div class="d-flex flex-wrap align-center">
                  <span class="mr-4" v-if="changesSummary.duplicatesResolved > 0">
                    <v-icon small color="success" class="mr-1">mdi-check</v-icon>
                    {{ changesSummary.rowsRemoved }} duplicate{{ changesSummary.rowsRemoved > 1 ? 's' : '' }} removed
                  </span>
                  <span class="mr-4" v-if="changesSummary.whitespaceCleaned > 0">
                    <v-icon small color="success" class="mr-1">mdi-check</v-icon>
                    {{ changesSummary.whitespaceCleaned }} formatting issue{{ changesSummary.whitespaceCleaned > 1 ? 's' :
                      '' }} fixed
                  </span>
                  <span v-if="changesSummary.valuesStandardized > 0">
                    <v-icon small color="success" class="mr-1">mdi-check</v-icon>
                    {{ changesSummary.valuesStandardized }} brand name{{ changesSummary.valuesStandardized > 1 ? 's' : '' }}
                    standardised
                  </span>
                </div>
              </v-alert>

              <v-alert type="info" outlined dense class="mb-4">
                Your original file has not been modified.
              </v-alert>

              <div class="d-flex align-center mt-6">
                <v-btn v-if="hasChanges" outlined color="primary" @click="$emit('download-fixed-file')">
                  <v-icon left small>mdi-download</v-icon>
                  Download Updated File
                </v-btn>

                <v-spacer></v-spacer>

                <v-btn color="primary" x-large :loading="isDryRunLoading" :disabled="!canImport" @click="$emit('start-import')">
                  <v-icon left>mdi-upload</v-icon>
                  Start Import
                </v-btn>
              </div>
            </template>

            <div v-if="isImporting" class="mt-6">
              <v-progress-linear :value="importProgress" height="24" striped color="primary">
                <template v-slot:default>
                  {{ importProgress }}%
                </template>
              </v-progress-linear>
              <div class="text-center mt-2 grey--text">
                {{ importStatus }}
              </div>
            </div>
          </template>

        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
export default {
  name: 'StepReview',
  data() {
    return {
      showDryRunDetails: false
    };
  },
  props: {
    summary: {
      type: Object,
      default: () => ({
        totalRows: 0,
        validRows: 0,
        newRecords: 0,
        updates: 0,
        sections: [],
        categories: []
      })
    },
    changesSummary: {
      type: Object,
      default: () => ({
        duplicatesResolved: 0,
        rowsRemoved: 0,
        whitespaceCleaned: 0,
        valuesStandardized: 0
      })
    },
    isImporting: {
      type: Boolean,
      default: false
    },
    activeImportAction: {
      type: String,
      default: null
    },
    importProgress: {
      type: Number,
      default: 0
    },
    importStatus: {
      type: String,
      default: ''
    },
    canImport: {
      type: Boolean,
      default: true
    },
    importComplete: {
      type: Boolean,
      default: false
    },
    lastRunWasDryRun: {
      type: Boolean,
      default: false
    },
    reportAvailable: {
      type: Boolean,
      default: false
    },
    canCommitDryRun: {
      type: Boolean,
      default: false
    },
    dryRunSummary: {
      type: Object,
      default: () => ({
        totalRows: 0,
        createCount: 0,
        updateCount: 0,
        skipCount: 0,
        errorCount: 0
      })
    },
    dryRunDetails: {
      type: Array,
      default: () => ([])
    },
    dryRunReportError: {
      type: String,
      default: null
    },
    importError: {
      type: String,
      default: null
    }
  },
  computed: {
    hasChanges() {
      return this.changesSummary.duplicatesResolved > 0 ||
        this.changesSummary.whitespaceCleaned > 0 ||
        this.changesSummary.valuesStandardized > 0;
    },
    commitBlockedAfterDryRun() {
      return this.lastRunWasDryRun && !this.canCommitDryRun;
    },
    isDryRunLoading() {
      return this.isImporting && this.activeImportAction === 'dryRun';
    },
    isCommitLoading() {
      return this.isImporting && this.activeImportAction === 'commitDryRun';
    }
  },
  methods: {
    detailsRowClass(item) {
      const action = String(item?.action || '').toLowerCase();
      const notes = String(item?.notes || '').toLowerCase();

      if (action.includes('error') || notes.includes('error') || notes.includes('failed')) {
        return 'details-row-error';
      }

      if (action.includes('create') || action.includes('add')) {
        return 'details-row-create';
      }

      if (action.includes('update') || action.includes('change')) {
        return 'details-row-update';
      }

      if (action.includes('skip') || action.includes('ignored') || action.includes('no change')) {
        return 'details-row-skip';
      }

      return '';
    }
  }
};
</script>

<style scoped>
.stat-card {
  transition: transform 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.result-screen {
  max-width: 1100px;
  margin: 0 auto;
}

.details-table-wrap {
  max-height: 420px;
  overflow: auto;
}

.details-row-create td {
  background-color: #e8f5e9;
}

.details-row-update td {
  background-color: #fff8e1;
}

.details-row-skip td {
  background-color: #f5f5f5;
}

.details-row-error td {
  background-color: #ffebee;
}
</style>
